"use client";

import { ContentfulImage } from "@components/ContentfulImage";
import Link from "@components/Link";
import { Accordion } from "@components/ngg";
import type { BrandPageFragment } from "@server/gql/graphql";

import { cn } from "@/lib/utils";

import Icon from "./icon";

export default function Brand({
  title,
  brands,
  readMore,
  locale,
  className,
}: {
  title: string;
  brands?: (BrandPageFragment | null)[];
  readMore: string;
  locale: string | undefined;
  className?: string;
}) {
  if (!brands?.length) return null;

  return (
    <Accordion
      IconComponent={Icon}
      WrapperComponent="div"
      title={title}
      classNames={{
        wrapper: className,
        button: "text-left items-center leading-4 p-4 uppercase rounded-none",
        panel: "p-4 lg:pb-8",
      }}
      transitionProps={{ appear: false }}
    >
      <>
        {brands.map((brand) => {
          if (!brand) {
            return null;
          }

          const hasBackgroundImage =
            brand.desktopBackground || brand.mobileBackground;

          return (
            <div
              className="relative grid grid-cols-[auto_1fr] gap-x-4 max-lg:gap-y-6 lg:gap-x-6"
              key={brand.sys.id}
            >
              <ContentfulImage
                className="flex aspect-square h-32 w-32 items-center justify-center bg-gray-100 object-cover"
                desktopImage={brand.desktopBackground}
                mobileImage={brand.mobileBackground}
                sizes="128px"
              />
              <div
                className={cn(
                  "flex flex-col gap-1 text-base max-lg:-order-1",
                  hasBackgroundImage && "max-lg:col-span-full",
                )}
              >
                {brand?.slug ? (
                  <Link
                    locale={locale}
                    className="font-normal"
                    href={brand.slug}
                  >
                    {brand?.title}
                  </Link>
                ) : (
                  <p className="font-normal">{brand?.title}</p>
                )}
                <p>{brand?.shortDescription}</p>
                {brand?.slug && (
                  <Link
                    locale={locale}
                    className="underline underline-offset-1"
                    href={brand.slug}
                  >
                    {readMore}
                  </Link>
                )}
              </div>
              <ContentfulImage
                desktopImage={brand.desktopImage}
                mobileImage={brand.mobileImage}
                className={cn(
                  "max-h-full object-contain mix-blend-multiply lg:col-start-2 lg:ml-auto",
                  !hasBackgroundImage
                    ? "col-start-1 col-end-1 row-start-1 row-end-1 aspect-square h-24 w-24 place-self-center self-stretch bg-gray-100 p-2 lg:h-32 lg:w-32"
                    : "max-w-24 place-self-center lg:absolute lg:bottom-0 lg:right-0",
                )}
                sizes="128px"
              />
            </div>
          );
        })}
      </>
    </Accordion>
  );
}
